<template>
  <v-menu
    v-model="menuOpen"
    bottom
    left
    offset-y
    :max-width="320"
    :content-class="contentClass"
    eager
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs">
        <!-- Guest (not logged in): mobile = 3 dots; desktop = account icon + "Ienākt" -->
        <template v-if="!isLoggedIn">
          <div v-if="isMobile" class="user-account-menu-activator user-account-menu-activator--mobile" :class="{ 'user-account-menu-activator--org': useOrgColor }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="user-account-menu-icon-btn"
              :title="$t('layout.menu.login')"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </div>
          <div v-else class="user-account-menu-activator user-account-menu-activator--desktop user-account-menu-activator--guest" :class="{ 'user-account-menu-activator--org': useOrgColor }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="user-account-menu-guest-btn"
              text
            >
              <v-icon class="user-account-menu-name-icon mr-2">mdi-account-circle-outline</v-icon>
              <span class="user-account-menu-name">{{ $t('layout.menu.login') }}</span>
            </v-btn>
          </div>
        </template>
        <!-- Logged in: desktop = name (click → account) + chevron (click → menu); mobile = icon (click → menu) -->
        <template v-else>
          <div v-if="isMobile" class="user-account-menu-activator user-account-menu-activator--mobile" :class="{ 'user-account-menu-activator--org': useOrgColor }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="user-account-menu-icon-btn"
              :title="$t('layout.header.menu.my-profile')"
            >
              <v-icon>mdi-account-circle</v-icon>
            </v-btn>
          </div>
          <div v-else class="user-account-menu-activator user-account-menu-activator--desktop" :class="{ 'user-account-menu-activator--org': useOrgColor }">
            <router-link
              :to="mypagesLink"
              class="user-account-menu-name-link"
            >
              <v-icon class="user-account-menu-name-icon">mdi-account-circle</v-icon>
              <span class="user-account-menu-name">{{ currentAccountName }}</span>
            </router-link>
            <span class="user-account-menu-separator" aria-hidden="true" />
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              text
              class="user-account-menu-chevron-btn"
            >
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </div>
        </template>
      </slot>
    </template>

    <v-list class="user-account-menu-list">
      <!-- Optional: Language section (parent controls via showLanguage) -->
      <template v-if="showLanguage">
        <v-subheader class="user-account-menu-section-title">{{ $t('layout.header.menu.language') }}</v-subheader>
        <v-list-item class="user-account-menu-lang-row">
          <v-list-item-content class="py-0">
            <div class="user-account-menu-lang-buttons">
              <v-btn
                v-for="lang in languages"
                :key="lang.value"
                @click="changeLang(lang.value)"
                tile
                plain
                small
                :class="{ 'user-account-menu-lang-active': lang.value === currentLanguage }"
                class="user-account-menu-lang-btn"
              >
                {{ lang.text }}
              </v-btn>
            </div>
          </v-list-item-content>
        </v-list-item>
        <v-divider />
      </template>

      <!-- Guest: Account section = Ienākt + Reģistrēties -->
      <template v-if="!isLoggedIn">
        <v-subheader class="user-account-menu-section-title">{{ $t('layout.header.menu.account') }}</v-subheader>
        <v-list-item @click="goToLogin" class="user-account-menu-item">
          <v-list-item-icon class="mr-2">
            <v-icon size="20">mdi-login</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="user-account-menu-item-title">{{ $t('layout.menu.login') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-icon size="20" class="user-account-menu-go-icon">mdi-arrow-right</v-icon>
          </v-list-item-action>
        </v-list-item>
        <v-list-item @click="goToSignup" class="user-account-menu-item">
          <v-list-item-icon class="mr-2">
            <v-icon size="20">mdi-account-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="user-account-menu-item-title">{{ $t('layout.menu.signup') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-icon size="20" class="user-account-menu-go-icon">mdi-arrow-right</v-icon>
          </v-list-item-action>
        </v-list-item>
      </template>

      <!-- Logged in: Account section = list of accounts -->
      <template v-else>
        <v-subheader class="user-account-menu-section-title">{{ $t('layout.header.menu.account') }}</v-subheader>
        <v-list-item-group color="primary" class="user-account-menu-accounts">
          <v-list-item
            v-for="account in allAccounts"
            :key="accountKey(account)"
            @click="switchAccount(account)"
            class="user-account-menu-item"
          >
            <v-list-item-avatar color="var(--color-pallet-1)" size="36">
              <v-icon v-if="!getAccountLogo(account)" color="white" size="20">
                {{ getAccountIcon(account) }}
              </v-icon>
              <img v-else :src="getAccountLogo(account)" :alt="account.displayName" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="user-account-menu-item-title">{{ account.displayName }}</v-list-item-title>
              <v-list-item-subtitle v-if="account.organizationId" class="user-account-menu-item-subtitle">
                {{ getRolesText(account.roles) }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-else class="user-account-menu-item-subtitle">
                {{ $t('layout.account-switcher.participant-account') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            
            <v-list-item-action>
              <v-icon size="20" class="user-account-menu-go-icon">mdi-arrow-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>

        <template v-if="showLogout">
          <v-divider />
          <v-list-item @click="logout" class="user-account-menu-item user-account-menu-logout">
            <v-list-item-icon class="mr-2">
              <v-icon color="error" size="20">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="user-account-menu-logout-text">{{ $t('layout.menu.logout') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
import { SUPPORTED_LOCALES } from '@/locales/locales'
import urlUtil from '@/js/utils/urlUtil'
import { OrganizationPath } from '@/router/organizationPaths'

export default {
  name: 'UserAccountMenu',
  props: {
    /** Show language selector (LV/EN/RU) at top of menu. Use in main layout only. */
    showLanguage: { type: Boolean, default: false },
    /** Show logout option at bottom of menu. Set false for mypages (account switcher only). */
    showLogout: { type: Boolean, default: true },
    /** 'light' = white panel (main/org header), 'dark' = dark panel (mypages sidebar) */
    variant: { type: String, default: 'light', validator: v => ['light', 'dark'].includes(v) }
  },
  data() {
    return {
      menuOpen: false
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn']
    },
    isMobile() {
      return this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smAndDown
    },
    isOrganizationPage() {
      return urlUtil.isOrganizationPath(this.$route.path)
    },
    /** Apply organization color when prop is set or when on organization page (route). */
    useOrgColor() {
      return this.isOrganizationPage
    },
    organizationVisitorPage() {
      return this.$store.getters['session/organizationByLocation']
    },
    mypagesLink() {
      const url = this.$store.getters['dashboard/currentAccountUrl']
      return url || urlUtil.getLocalisedMyPagesUrl()
    },
    contentClass() {
      return this.variant === 'dark'
        ? 'user-account-menu-content user-account-menu-content--dark'
        : 'user-account-menu-content user-account-menu-content--light'
    },
    allAccounts() {
      return this.$store.getters['user/accountsForSwitcher'] || []
    },
    currentAccount() {
      return this.$store.getters['dashboard/currentAccount']
    },
    currentAccountName() {
      return this.currentAccount ? this.currentAccount.displayName : (this.$store.getters['user/name'] || '')
    },
    currentAccountIcon() {
      if (!this.currentAccount) return 'mdi-account'
      return this.currentAccount.organizationId ? 'mdi-domain' : 'mdi-account'
    },
    currentAccountColor() {
      if (!this.currentAccount) return 'grey'
      return this.currentAccount?.organizationId ? 'var(--color-pallet-1)' : '#1976D2'
    },
    currentAccountLogo() {
      if (!this.currentAccount) return null
      return this.currentAccount.src_logo_large || this.currentAccount.logo_url || this.currentAccount.logo || this.currentAccount.logoUrl || null
    },
    currentLanguage() {
      return this.$i18n.locale
    },
    languages() {
      return SUPPORTED_LOCALES.map(lang => ({ value: lang.code, text: lang.name }))
    }
  },
  methods: {
    accountKey(account) {
      return account.organizationIdentifier ?? account.organizationId ?? 'personal'
    },
    isCurrentAccount(account) {
      if (!this.currentAccount) return false
      return this.currentAccount.type === account.type && this.currentAccount.organizationId === account.organizationId
    },
    getAccountIcon(account) {
      return account.organizationId ? 'mdi-domain' : 'mdi-account'
    },
    getAccountLogo(account) {
      return account?.logo_url ?? null
    },
    getRolesText(roles) {
      if (!roles || roles.length === 0) return ''
      return roles.map(role => this.$t(`layout.account-switcher.role.${role.toLowerCase()}`)).join(', ')
    },
    switchAccount(account) {
      if (account.account_url) {
        this.$router.push(account.account_url)
      }
      this.menuOpen = false
    },
    goToLogin() {
      const url = this.isOrganizationPage ? urlUtil.getLocalisedOrganizationUrl(this.organizationVisitorPage, 'login') : urlUtil.getLocalisedUrl('login')
      this.$router.push(url)
      this.menuOpen = false
    },
    goToSignup() {
      const url = this.isOrganizationPage ? urlUtil.getLocalisedOrganizationUrl(this.organizationVisitorPage, OrganizationPath.SIGNUP_PARTICIPANT) : urlUtil.getLocalisedUrl('signup')
      this.$router.push(url)
      this.menuOpen = false
    },
    logout() {
      this.$store.dispatch('user/logout')
      this.menuOpen = false
    },
    changeLang(lang) {
      localStorage.setItem('lang', lang)
      const urlInDiffLanguage = urlUtil.getUrlInLocale(lang, urlUtil.getUrlWithoutLocale())
      window.open(urlInDiffLanguage, '_self')
    }
  }
}
</script>

<style scoped>
/* Default activator: one visual button with two parts (desktop) or icon only (mobile) */
.user-account-menu-activator {
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.user-account-menu-activator--desktop {
  cursor: default;
  gap: 0;
}

/* Guest desktop: single button with icon + "Ienākt" (light background, primary color) */
.user-account-menu-activator--guest .user-account-menu-guest-btn {
  font-weight: 500;
  border-radius: 6px;
  color: var(--color-pallet-1) !important;
}
.user-account-menu-activator--guest .user-account-menu-guest-btn .v-icon {
  color: var(--color-pallet-1) !important;
}
.user-account-menu-activator--guest.user-account-menu-activator--org .user-account-menu-guest-btn .v-icon {
  color: var(--organization-color-secondary) !important;
}
.user-account-menu-activator--guest.user-account-menu-activator--org .user-account-menu-guest-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

/* Vertical separator between account name and chevron */
.user-account-menu-separator {
  width: 1px;
  align-self: stretch;
  min-height: 24px;
  margin: 4px 0;
  background: rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.user-account-menu-activator--desktop:not(.user-account-menu-activator--org) .user-account-menu-separator {
  background: rgba(255, 255, 255, 0.25);
}

.user-account-menu-activator--org .user-account-menu-separator {
  background: rgba(0, 0, 0, 0.2);
}

/* Name part: no hover = just text; hover = darker shade of area, grey background without dense, no button down */
.user-account-menu-name-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  padding: 8px 12px;
  border-radius: 6px 0 0 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 8px;
}

.user-account-menu-name-link:hover {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

.user-account-menu-activator--org .user-account-menu-name-link:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

/* Organization color (when provided): account icon, name and chevron */
.user-account-menu-activator--org .user-account-menu-name-icon,
.user-account-menu-activator--org .user-account-menu-name,
.user-account-menu-activator--org .user-account-menu-chevron-btn .v-icon,
.user-account-menu-activator--org .user-account-menu-icon-btn .v-icon {
  color: var(--organization-color-secondary) !important;
}

.user-account-menu-name-icon {
  margin-right: 0;
  flex-shrink: 0;
}

.user-account-menu-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Chevron part: same hover – darker shade, grey background without dense, no button down */
.user-account-menu-chevron-btn {
  min-width: 40px !important;
  border-radius: 0 6px 6px 0 !important;
  transition: background-color 0.2s ease;
}

.user-account-menu-chevron-btn:hover {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

.user-account-menu-activator--org .user-account-menu-chevron-btn:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

.user-account-menu-chevron-btn::before {
  background: transparent !important;
}

.user-account-menu-icon-btn {
  min-width: 48px !important;
  min-height: 48px !important;
  transition: background-color 0.2s ease;
}

.user-account-menu-activator--org .user-account-menu-icon-btn:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

.user-account-menu-list {
  max-height: 420px;
  overflow-y: auto;
  padding: 8px 0 !important;
}

.user-account-menu-section-title {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 8px 16px 4px !important;
  height: auto !important;
}

.user-account-menu-lang-row {
  min-height: 0 !important;
  padding: 0 8px 8px !important;
}

.user-account-menu-lang-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.user-account-menu-lang-btn {
  min-width: 40px !important;
}

.user-account-menu-lang-active {
  background: rgba(var(--v-primary-base), 0.12) !important;
  color: var(--color-pallet-1) !important;
}

.user-account-menu-accounts {
  padding: 0 !important;
}

.user-account-menu-item {
  min-height: 52px !important;
}

.user-account-menu-item-active {
  background: rgba(var(--v-primary-base), 0.08) !important;
}

.user-account-menu-item-title {
  font-weight: 500 !important;
}

.user-account-menu-item-subtitle {
  font-size: 12px !important;
  opacity: 0.85;
}

.user-account-menu-logout {
  margin-top: 4px;
}

.user-account-menu-logout-text {
  font-weight: 500 !important;
}

.user-account-menu-go-icon {
  opacity: 0.6;
}
</style>

<style>
/* Content is teleported to body */
.user-account-menu-content {
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.user-account-menu-content--light {
  background: #fff !important;
}

.user-account-menu-content--light .v-list {
  background: transparent !important;
}

.user-account-menu-content--light .v-subheader,
.user-account-menu-content--light .user-account-menu-section-title {
  color: rgba(0, 0, 0, 0.6) !important;
}

.user-account-menu-content--light .v-list-item__title,
.user-account-menu-content--light .v-list-item__subtitle {
  color: rgba(0, 0, 0, 0.87) !important;
}

.user-account-menu-content--light .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

.user-account-menu-content--light .user-account-menu-item-active {
  background: rgba(var(--v-primary-base), 0.08) !important;
}

.user-account-menu-content--dark {
  background: linear-gradient(180deg, var(--color-pallet-5) 0%, var(--color-pallet-6) 100%) !important;
}

.user-account-menu-content--dark .v-list {
  background: transparent !important;
}

.user-account-menu-content--dark .v-subheader,
.user-account-menu-content--dark .user-account-menu-section-title {
  color: rgba(255, 255, 255, 0.85) !important;
}

.user-account-menu-content--dark .v-list-item__title,
.user-account-menu-content--dark .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.95) !important;
}

.user-account-menu-content--dark .v-list-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.user-account-menu-content--dark .user-account-menu-item-active {
  background: rgba(255, 255, 255, 0.12) !important;
}

.user-account-menu-content--dark .user-account-menu-logout .v-list-item__title {
  color: rgba(255, 255, 255, 0.9) !important;
}

.user-account-menu-content--dark .user-account-menu-logout:hover {
  background: rgba(244, 67, 54, 0.15) !important;
}
</style>
